import fetch from 'isomorphic-fetch';

import metricsService from '../services/metrics';
import { rumService } from '../services/rum';
import { RUM_EVENT_STATUS } from '../constants/metrics';
import logger from './logger';
import { retry } from './promise';
import { AppConfig } from '../types/app';
import { resolveIdpConfigUrls, resolveProfileUrls } from './endpointHelper';

const REQUEST_TIMEOUT_MS = 10000;
const APIGW_REQUEST_ID_HEADER = 'x-amzn-requestid';

export class HttpError extends Error {
  constructor(public readonly status: number, statusText: string) {
    super(`${status}: ${statusText}`);
  }
}

export const isRetryableError = (error: Error): boolean => {
  if (error instanceof HttpError) {
    return error.status === 429 || error.status >= 500;
  }
  // Network failures: browser fetch throws TypeError,
  // node-fetch/isomorphic-fetch throws FetchError,
  // AbortController throws DOMException with name 'AbortError'
  if (error instanceof TypeError) return true;
  if (error.name === 'FetchError' || error.name === 'AbortError') return true;
  return false;
};

type RequestOptions = {
  treat404AsOk?: boolean;
  treat400AsOk?: boolean;
  logWithRum?: boolean;
  logWithKatal?: boolean;
  metricNs: string;
};

/**
 * API wrapper which emits metrics and applies a retry strategy.
 * Uses either Katal metrics or RUM logging based on the logWithRum flag or both
 */
export const request = async (
  url: string,
  fetchOpts: RequestInit = {},
  options: RequestOptions
): Promise<Response> => {
  const {
    treat404AsOk = false,
    treat400AsOk = false,
    logWithRum = false,
    logWithKatal = true, // Default to true for backward compatibility
    metricNs,
  } = options;

  // Initialize logging based on flags
  const publisher = logWithKatal ? metricsService.getPublisher(metricNs) : null;
  const totalTimer = logWithKatal
    ? metricsService.createTimerStopWatch(`${metricNs}Time`).withMonitor()
    : null;
  const useKatal = logWithKatal && publisher;
  const startTime = logWithRum ? Date.now() : 0;

  return await retry(
    () => {
      const requestTimer = useKatal
        ? metricsService
            .createTimerStopWatch(`${metricNs}RequestTime`)
            .withMonitor()
        : null;
      const requestStartTime = logWithRum ? Date.now() : 0;

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );

      return fetch(url, { ...fetchOpts, signal: controller.signal })
        .then((resp: Response) => {
          const is404Ok = resp.status === 404 && treat404AsOk;
          const is400Ok = resp.status === 400 && treat400AsOk;
          if (resp.ok || is404Ok || is400Ok) {
            // Log success
            if (logWithRum) {
              rumService.recordEvent(`${metricNs}${RUM_EVENT_STATUS.SUCCESS}`, {
                url,
                status: resp.status,
                duration: Date.now() - requestStartTime,
                totalDuration: Date.now() - startTime,
                requestId:
                  resp.headers.get(APIGW_REQUEST_ID_HEADER) ?? undefined,
              });
            }
            if (useKatal) {
              publisher!.publishCounterMonitor(`${metricNs}Success`, 1);
            }
            return resp;
          }

          logger.error(`Endpoint [${url}] returns an error response: ${resp}`);

          if (useKatal) {
            publisher!.publishStringTruncate(
              `${metricNs}ErrorCode`,
              `${resp.status}`
            );
          }
          throw new HttpError(resp.status, resp.statusText);
        })
        .catch((error: Error) => {
          logger.error('Encountered an expected error:', error);

          if (logWithRum) {
            rumService.recordEvent(
              `${metricNs}${RUM_EVENT_STATUS.REQUEST_ERROR}`,
              {
                url,
                error: error.message,
                duration: Date.now() - requestStartTime,
                userAgent: navigator.userAgent,
                path: `${window.location.pathname}${window.location.search}`,
              }
            );
          }

          if (useKatal) {
            publisher!.publishCounterMonitor(`${metricNs}RequestError`, 1);
            publisher!.publishStringTruncate(
              `${metricNs}ErrorMsg`,
              error.message || 'NA'
            );
            publisher!.publishStringTruncate(
              `${metricNs}ErrorUri`,
              `${window.location.pathname}${window.location.search}`
            );
            publisher!.publishStringTruncate(
              `${metricNs}UserAgent`,
              navigator.userAgent
            );
          }

          throw error;
        })
        .finally(() => {
          clearTimeout(timeoutId);
          if (useKatal && requestTimer) {
            publisher!.publish(requestTimer);
          }
        });
    },
    {
      retries: 3,
      interval: 200,
      retryPredicate: isRetryableError,
    }
  )
    .catch((error: Error) => {
      // Captures when an error is impacting the customer experience, after
      // retries are exhausted.
      if (logWithRum) {
        rumService.recordEvent(`${metricNs}${RUM_EVENT_STATUS.FINAL_ERROR}`, {
          url,
          error: error.message,
          totalDuration: Date.now() - startTime,
        });
      }
      if (useKatal) {
        publisher!.publishCounterMonitor(`${metricNs}Error`, 1);
      }
      throw error;
    })
    .finally(() => {
      if (useKatal && totalTimer) {
        publisher!.publish(totalTimer);
      }
    });
};

export const requestWithFallbackUrl = async (
  url: string,
  fallbackUrl: string,
  fetchOpts: RequestInit = {},
  options: RequestOptions
): Promise<Response> => {
  try {
    return await request(url, fetchOpts, {
      ...options,
      logWithRum: true,
    });
  } catch (e) {
    // Don't fallback on 409 — the resource state won't change on a different URL
    if (e instanceof HttpError && e.status === 409) {
      throw e;
    }
    logger.warn(
      `Request to URL '${url}' failed. Retrying with fallback URL '${fallbackUrl}'`,
      e
    );
    return await request(fallbackUrl, fetchOpts, {
      ...options,
      metricNs: `${options.metricNs}Fallback`,
      logWithRum: true,
    });
  }
};

// This function is helpful to join endpoint and API path
export function joinUrlPath(a: string, b: string) {
  return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
}

/**
 * Makes a Profile API request with custom domain fallback support.
 * Resolves the appropriate endpoint URLs and applies the fallback strategy.
 */
export async function profileRequest(
  config: AppConfig,
  path: string,
  fetchOpts: RequestInit,
  options: RequestOptions
): Promise<Response> {
  const { defaultUrl, customDomainUrl, useCustomDomain } = resolveProfileUrls(
    config,
    path
  );

  return useCustomDomain
    ? requestWithFallbackUrl(customDomainUrl!, defaultUrl, fetchOpts, options)
    : request(defaultUrl, fetchOpts, options);
}

/**
 * Makes an IDPConfig Publisher API request with custom domain fallback support.
 * Resolves the appropriate endpoint URLs and applies the fallback strategy.
 */
export async function idpConfigRequest(
  config: AppConfig,
  path: string,
  fetchOpts: RequestInit,
  options: RequestOptions
): Promise<Response> {
  const { defaultUrl, customDomainUrl, useCustomDomain } = resolveIdpConfigUrls(
    config,
    path
  );

  return useCustomDomain
    ? requestWithFallbackUrl(customDomainUrl!, defaultUrl, fetchOpts, options)
    : request(defaultUrl, fetchOpts, options);
}
